import React, { useEffect, useState } from 'react'
import ArrowIcon from '../../elements/icons/ArrowIcon'
import { useDispatch, useSelector } from 'react-redux';
import { ERROR, SET_SUCCESS } from '../../../store/types';
import { authHeader, baseUrl } from '../../../utils';
import axios from 'axios';
import Loader from '../../elements/Loader';
import InfoIcon from '../../elements/icons/InfoIcon';
import TrashIcon from '../../elements/icons/TrashIcon';
import PlusIcon from '../../elements/icons/PlusIcon';
import ChevronIcon from '../../elements/icons/ChevronIcon';
import TextField from '../../elements/form/TextField';
import SelectField from '../../elements/form/SelectField';
import ModalDialog from '../../layouts/ModalDialog';
import ModalActionButton from '../../elements/ModalActionButton';
import { fetchStudentTraits } from '../../../store/actions/traitsActions';
import ManageStudentTraits from './ManageStudentTraits';
import ErrorState from '../../elements/ErrorState';
// import { set } from 'date-fns';

const StudentResultsCompilation =({student, toggleTab}) => {

    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch()
    const traitsSelector = useSelector(state => state.traits);
    const [compiledResult, setCompiledResult] = useState(null);
    const [currentTerm, setCurrentTerm] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [confirmModalShown, setConfirmModalShown] = useState(false);
    const [subjectModal, setSubjectModal] = useState({ shown: false, areaKey: null });
    const [subjectModalSubject, setSubjectModalSubject] = useState(null);
    const [subjectModalComment, setSubjectModalComment] = useState('');
    const [traitsModalShown, setTraitsModalShown] = useState(false);
    // const [newTraitName, setNewTraitName] = useState('');
    // const [newTraitComment, setNewTraitComment] = useState('');
    const [sectionsOpen, setSectionsOpen] = useState({
        issues: true,
        subjectBreakdown: true,
        areasOfStrength: true,
        areasNeedingImprovement: true,
        traits: true,
        overallComments: true
    });

    const toggleSection = (sectionKey) => {
        setSectionsOpen(previous => ({ ...previous, [sectionKey]: !previous[sectionKey] }))
    }

    const SectionHeader = ({ title, sectionKey, action }) => (
        <div className='w-full flex items-center justify-between mb-3'>
            <button onClick={()=>{toggleSection(sectionKey)}} className='flex items-center gap-x-2 text-sm text-gray-600 font-space-grotesk font-medium'>
                <ChevronIcon className={`w-4 h-4 transition duration-200 ${sectionsOpen[sectionKey] ? 'rotate-[270deg]' : 'rotate-180'}`} />
                {title}
            </button>
            {action}
        </div>
    )

    const [reload, setReload] = useState(0);
    const [errorMessage, setErrorMessage] = useState('')

    useEffect(() => {
        dispatch(fetchStudentTraits(`student=${student._id}`,0, 0))

        const findCurrentTerm = async () => {
            try {
                setLoading(true)
                const response = await axios.get(`${baseUrl}/terms?currentlyActive=true&expand=session`, { headers: authHeader() });
                const term = response.data.data.terms[0];
                setCurrentTerm(term);
                fetchResultCompilation(term._id);

            } catch (error) {
                console.error('Error finding current term: ', error);
                dispatch({
                    type: ERROR,
                    error
                })
            }
        };

        const fetchResultCompilation = async (termId) => {
            try {
                const headers = authHeader()
                
                setLoading(true)
                const response = await axios.get(`${baseUrl}/student-results/compile/${student._id}/${termId}`, { headers })
                const result = response.data.data
                // traits are stored as one flat list here; the backend separates them into strength/improvement on submission
                setCompiledResult({
                    ...result,
                    traits: [...(result.areasOfStrength?.traits || []), ...(result.areasNeedingImprovement?.traits || [])]
                });
            } catch (error) {
                console.error('Error fetching result compilation: ', error);
                dispatch({
                    type: ERROR,
                    error
                })
                setErrorMessage(error?.response?.data?.message || 'An error occurred while fetching the result compilation.')
            } finally {
                setLoading(false);
            }
        };

        findCurrentTerm();
        
        return () => {
            
        };
    }, [dispatch, student._id, reload]);
    
    // Options for pickers, derived from the compiled subject breakdown
    const subjectOptions = (compiledResult?.subjectBreakdown || []).map(entry => ({
        _id: entry.subject._id,
        name: entry.subject.name
    }))

    const subjectName = (subjectId) => subjectOptions.find(s => s._id === subjectId)?.name || 'Unknown subject'

    const updateCompiledResult = (patch) => {
        setCompiledResult(previous => ({ ...previous, ...patch }))
    }

    // ---- Overall comments (array of strings) ----
    const addOverallComment = () => {
        updateCompiledResult({ overallComments: [...(compiledResult.overallComments || []), ''] })
    }

    const updateOverallComment = (index, value) => {
        const temp = [...compiledResult.overallComments]
        temp[index] = value
        updateCompiledResult({ overallComments: temp })
    }

    const removeOverallComment = (index) => {
        const temp = [...compiledResult.overallComments]
        temp.splice(index, 1)
        updateCompiledResult({ overallComments: temp })
    }

    // ---- Areas of strength / needing improvement (subjects) ----
    const openSubjectModal = (areaKey) => {
        setSubjectModalSubject(null)
        setSubjectModalComment('')
        setSubjectModal({ shown: true, areaKey })
    }

    const closeSubjectModal = () => {
        setSubjectModal({ shown: false, areaKey: null })
    }

    const confirmAddAreaSubject = () => {
        if (!subjectModalSubject) return
        const area = compiledResult[subjectModal.areaKey]
        updateCompiledResult({
            [subjectModal.areaKey]: {
                ...area,
                subjects: [...(area.subjects || []), { subject: subjectModalSubject._id, comment: subjectModalComment }]
            }
        })
        closeSubjectModal()
    }

    const updateAreaSubjectComment = (areaKey, index, value) => {
        const area = compiledResult[areaKey]
        const temp = [...area.subjects]
        temp[index] = { ...temp[index], comment: value }
        updateCompiledResult({ [areaKey]: { ...area, subjects: temp } })
    }

    const removeAreaSubject = (areaKey, index) => {
        const area = compiledResult[areaKey]
        const temp = [...area.subjects]
        temp.splice(index, 1)
        updateCompiledResult({ [areaKey]: { ...area, subjects: temp } })
    }

    // ---- Traits (flat list; backend separates into strength/improvement on submission) ----
    const openTraitsModal = () => {
        // setNewTraitName('')
        // setNewTraitComment('')
        setTraitsModalShown(true)
    }

    const closeTraitsModal = () => {
        setTraitsModalShown(false)
    }

    // const addTraitRecord = () => {
    //     if (!newTraitName || newTraitName === '') return
    //     updateCompiledResult({ traits: [...(compiledResult.traits || []), { trait: newTraitName, comment: newTraitComment }] })
    //     setNewTraitName('')
    //     setNewTraitComment('')
    // }

    // const updateTraitComment = (index, value) => {
    //     const temp = [...compiledResult.traits]
    //     temp[index] = { ...temp[index], comment: value }
    //     updateCompiledResult({ traits: temp })
    // }

    // const removeTraitRecord = (index) => {
    //     const temp = [...compiledResult.traits]
    //     temp.splice(index, 1)
    //     updateCompiledResult({ traits: temp })
    // }

    const requestConfirmResults = () => {
        setConfirmModalShown(true)
    }

    const confirmResults = async () => {
        try {
            setConfirming(true)
            const headers = authHeader()

            const payload = {
                ...compiledResult,
                subjectBreakdown: compiledResult.subjectBreakdown?.map(entry => ({
                    ...entry,
                    subject: entry.subject?._id || entry.subject
                })),
                traits: traitsSelector?.studentTraits?.studentTraits[0]?.traits || []
            }
            await axios.post(`${baseUrl}/student-results/confirm`, payload, { headers })
            setConfirmModalShown(false)
            dispatch({
                type: SET_SUCCESS,
                payload: 'Result confirmed and saved successfully'
            })
            toggleTab('academics')
        } catch (error) {
            console.error('Error confirming result: ', error);
            dispatch({
                type: ERROR,
                error
            })
        } finally {
            setConfirming(false)
        }
    }

    return (
        <>
            <button onClick={()=>{toggleTab('academics')}} className="flex mt-2 mb-4 text-[13px] font-medium font-space-grotesk items-center gap-x-2 text-gray-400 hover:text-gray-700 transition duration-400">
                <ArrowIcon className="w-4 h-4 -rotate-180" />
                Back to Student Academics
            </button>
            <div className="w-full">
                <div className='pb-3 mb-2'>
                    <div className="w-full mx-auto">
                        <div className='w-full flex items-start justify-between'>
                            <div className='w-10/12'>
                                <h4 className='font-medium text-lg text-gray-600'>Student Result Compilation for {currentTerm?.name}</h4>
                                <div className=''>
                                    <p className='text-gray-600 text-sm'>
                                        Compile results for this student for the current term.
                                    </p>
                                </div>
                            </div>
                            {compiledResult && <div className='w-full flex flex-row-reverse gap-x-[5px]'>
                                <button disabled={confirming} onClick={()=>{requestConfirmResults()}} className='disabled:opacity-50 disabled:cursor-not-allowed rounded-lg px-4 py-3 text-sm text-purple-100 bg-vcm-purple transition duration-200 border border-vcm-purple hover:bg-vcm-light-purple hover:text-vcm-purple flex items-center font-space-grotesk justify-center gap-x-2'>
                                    Confirm Result
                                </button>
                            </div>}
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full">
                {loading ?
                    <Loader />
                : !compiledResult ?
                    <ErrorState
                        errorStateTitle="Sorry, unable to compile results"
                        errorStateText={<span className="capitalize">{errorMessage}</span>}
                    />
                :
                    <div className='w-full flex flex-col gap-y-6'>

                        {compiledResult.issues?.length > 0 ? <div className='w-full rounded-[8px] border border-amber-300 bg-amber-50 p-4'>
                            <div className='w-full flex items-start gap-x-3'>
                                <InfoIcon classes="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div className='w-full'>
                                    <button onClick={()=>{toggleSection('issues')}} className='w-full flex items-center justify-between'>
                                        <p className='text-sm font-semibold text-amber-800 font-space-grotesk'>This compilation has {compiledResult.issues.length} issue{compiledResult.issues.length > 1 ? 's' : ''}</p>
                                        <ChevronIcon className={`w-4 h-4 text-amber-600 transition duration-200 ${sectionsOpen.issues ? 'rotate-[270deg]' : 'rotate-180'}`} />
                                    </button>
                                    {sectionsOpen.issues && <ul className='mt-2 list-disc list-inside text-sm text-amber-700 space-y-1'>
                                        {compiledResult.issues.map((issue, issueIndex) => (
                                            <li key={issueIndex}>{typeof issue === 'string' ? issue : issue.message}</li>
                                        ))}
                                    </ul>}
                                </div>
                            </div>
                        </div>
                        :
                        <div className='w-full rounded-[8px] border border-green-300 bg-green-50 p-4'>
                            <div className='w-full flex items-start gap-x-3'>
                                <InfoIcon classes="w-5 h-5 text-green-600 flex-shrink-0 mt-0.75" />
                                <p className='text-sm font-semibold text-green-800 font-space-grotesk'>No issues found for this result compilation.</p>
                            </div>
                        </div>
                        }

                        <div className='w-full grid grid-cols-4 gap-4'>
                            <div className='rounded-[8px] border border-gray-100 bg-gray-50 p-4'>
                                <p className='text-xs text-gray-500 font-space-grotesk'>Academic Session</p>
                                <p className='text-sm font-semibold text-gray-700 mt-1'>{compiledResult.academicSession?.name}</p>
                            </div>
                            <div className='rounded-[8px] border border-gray-100 bg-gray-50 p-4'>
                                <p className='text-xs text-gray-500 font-space-grotesk'>Term</p>
                                <p className='text-sm font-semibold text-gray-700 mt-1'>{compiledResult.term?.name}</p>
                            </div>
                            <div className='rounded-[8px] border border-gray-100 bg-gray-50 p-4'>
                                <p className='text-xs text-gray-500 font-space-grotesk'>Term Total / Average</p>
                                <p className='text-sm font-semibold text-gray-700 mt-1'>{compiledResult.assessmentScores?.termTotal?.toFixed(2)} &middot; {compiledResult.assessmentScores?.termAverage?.toFixed(2)}%</p>
                            </div>
                            <div className='rounded-[8px] border border-gray-100 bg-gray-50 p-4'>
                                <p className='text-xs text-gray-500 font-space-grotesk'>Overall Average</p>
                                <p className='text-sm font-semibold text-gray-700 mt-1'>{compiledResult.overallAverage?.toFixed(2)}%</p>
                            </div>
                        </div>

                        <div className='w-full flex items-center gap-x-3'>
                            <span className={`inline-block text-xs px-2 py-1 rounded font-outfit ${compiledResult.finalTermResult ? 'bg-green-600 text-green-800 bg-opacity-10' : 'bg-gray-600 text-gray-500 bg-opacity-10'}`}>
                                {compiledResult.finalTermResult ? 'Final Term Result' : 'Not Final Term Result'}
                            </span>
                            <span className={`inline-block text-xs px-2 py-1 rounded font-outfit ${compiledResult.promoted ? 'bg-green-600 text-green-800 bg-opacity-10' : 'bg-gray-600 text-gray-500 bg-opacity-10'}`}>
                                {compiledResult.promoted ? 'Promoted' : 'Not Promoted'}
                            </span>
                        </div>

                        <div className='w-full'>
                            <SectionHeader title="Subject Breakdown" sectionKey="subjectBreakdown" />
                            {sectionsOpen.subjectBreakdown && <div className='w-full border border-gray-100 rounded-[8px] overflow-x-auto'>
                                <table className='w-full text-sm'>
                                    <thead>
                                        <tr className='bg-gray-50 text-left text-xs text-gray-500 font-space-grotesk'>
                                            <th className='px-4 py-3 font-medium'>Subject</th>
                                            <th className='px-4 py-3 font-medium'>Assessments</th>
                                            <th className='px-4 py-3 font-medium'>Total Score</th>
                                            <th className='px-4 py-3 font-medium'>Average</th>
                                            <th className='px-4 py-3 font-medium'>Grade</th>
                                            <th className='px-4 py-3 font-medium'>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className='divide-y divide-gray-100'>
                                        {compiledResult.subjectBreakdown?.map((entry, entryIndex) => (
                                            <tr key={entryIndex}>
                                                <td className='px-4 py-3 text-gray-700 font-medium'>{entry.subject.name}</td>
                                                <td className='px-4 py-3 text-gray-500'>{entry.uploadedAssessmentCount} of {entry.assessmentCount}</td>
                                                <td className='px-4 py-3 text-gray-500'>{entry.totalScore?.toFixed(2)}</td>
                                                <td className='px-4 py-3 text-gray-500'>{entry.averageScore?.toFixed(2)}%</td>
                                                <td className='px-4 py-3 text-gray-500'>{entry.grade}</td>
                                                <td className='px-4 py-3'>
                                                    <span className={`inline-block text-xs px-2 py-1 rounded font-outfit ${entry.passingGrade ? 'bg-green-600 text-green-800 bg-opacity-10' : 'bg-red-600 text-red-800 bg-opacity-10'}`}>
                                                        {entry.passingGrade ? 'Passing' : 'Failing'}
                                                    </span>
                                                    {entry.needsImprovement && <span className='inline-block ml-2 text-xs px-2 py-1 rounded font-outfit bg-amber-600 text-amber-800 bg-opacity-10'>
                                                        Needs Improvement
                                                    </span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>}
                        </div>

                        <div className='w-full rounded-[8px] border border-gray-100 p-4'>
                            <SectionHeader
                                title="Areas of Strength"
                                sectionKey="areasOfStrength"
                                action={<button onClick={()=>{openSubjectModal('areasOfStrength')}} className='flex items-center gap-x-1 text-xs text-gray-500 hover:text-gray-700'>
                                    <PlusIcon className='w-4 h-4' /> Add
                                </button>}
                            />
                            {sectionsOpen.areasOfStrength && <div className='flex flex-col gap-y-3'>
                                {compiledResult.areasOfStrength?.subjects?.length === 0 && <p className='text-xs text-gray-400'>No areas of strength recorded.</p>}
                                {compiledResult.areasOfStrength?.subjects?.map((item, itemIndex) => (
                                    <div key={itemIndex} className='flex items-center gap-x-2'>
                                        <div className='w-[35%]'>
                                            <TextField
                                                inputLabel=""
                                                fieldId={`strength-subject-${itemIndex}`}
                                                hasError={false}
                                                disabled={true}
                                                preloadValue={subjectName(item.subject)}
                                                returnFieldValue={()=>{}}
                                            />
                                        </div>
                                        <div className='flex-1'>
                                            <TextField
                                                inputLabel=""
                                                fieldId={`strength-comment-${itemIndex}`}
                                                hasError={false}
                                                preloadValue={item.comment || ''}
                                                inputPlaceholder="Comment"
                                                returnFieldValue={(value)=>{updateAreaSubjectComment('areasOfStrength', itemIndex, value)}}
                                            />
                                        </div>
                                        <button onClick={()=>{removeAreaSubject('areasOfStrength', itemIndex)}} className='flex-shrink-0 text-gray-400 hover:text-red-600'>
                                            <TrashIcon className='w-4 h-4' />
                                        </button>
                                    </div>
                                ))}
                            </div>}
                        </div>

                        <div className='w-full rounded-[8px] border border-gray-100 p-4'>
                            <SectionHeader
                                title="Areas Needing Improvement"
                                sectionKey="areasNeedingImprovement"
                                action={<button onClick={()=>{openSubjectModal('areasNeedingImprovement')}} className='flex items-center gap-x-1 text-xs text-gray-500 hover:text-gray-700'>
                                    <PlusIcon className='w-4 h-4' /> Add
                                </button>}
                            />
                            {sectionsOpen.areasNeedingImprovement && <div className='flex flex-col gap-y-3'>
                                {compiledResult.areasNeedingImprovement?.subjects?.length === 0 && <p className='text-xs text-gray-400'>No areas needing improvement recorded.</p>}
                                {compiledResult.areasNeedingImprovement?.subjects?.map((item, itemIndex) => (
                                    <div key={itemIndex} className='flex items-center gap-x-2'>
                                        <div className='w-[35%]'>
                                            <TextField
                                                inputLabel=""
                                                fieldId={`improvement-subject-${itemIndex}`}
                                                hasError={false}
                                                disabled={true}
                                                preloadValue={subjectName(item.subject)}
                                                returnFieldValue={()=>{}}
                                            />
                                        </div>
                                        <div className='flex-1'>
                                            <TextField
                                                inputLabel=""
                                                fieldId={`improvement-comment-${itemIndex}`}
                                                hasError={false}
                                                preloadValue={item.comment || ''}
                                                inputPlaceholder="Comment"
                                                returnFieldValue={(value)=>{updateAreaSubjectComment('areasNeedingImprovement', itemIndex, value)}}
                                            />
                                        </div>
                                        <button onClick={()=>{removeAreaSubject('areasNeedingImprovement', itemIndex)}} className='flex-shrink-0 text-gray-400 hover:text-red-600'>
                                            <TrashIcon className='w-4 h-4' />
                                        </button>
                                    </div>
                                ))}
                            </div>}
                        </div>

                        <div className='w-full rounded-[8px] border border-gray-100 p-4'>
                            <SectionHeader
                                title="Traits"
                                sectionKey="traits"
                                action={<button onClick={openTraitsModal} className='flex items-center gap-x-1 text-xs text-gray-500 hover:text-gray-700'>
                                    <PlusIcon className='w-4 h-4' /> Manage Traits
                                </button>}
                            />
                            {sectionsOpen.traits && (
                                !traitsSelector.studentTraits?.studentTraits?.[0] || traitsSelector.studentTraits?.studentTraits[0]?.traits?.length=== 0 ?
                                    <p className='text-xs text-gray-400'>No trait records yet.</p>
                                :
                                    <div className='w-full grid grid-cols-4 gap-4'>
                                        {traitsSelector.studentTraits?.studentTraits[0]?.traits?.map((item, itemIndex) => (
                                            <div key={itemIndex} className='rounded-[8px] bg-gray-50 p-3 flex items-center justify-between'>
                                                <p className='text-sm font-medium text-gray-700'>{item.trait.name} 
                                                </p>
                                                <span className={`px-3 py-1 rounded text-xs font-space-grotesk font-semibold ${item.score > 10 || item.score < 5 ? 'bg-red-600/10 text-red-600' : item.score < 7 && item.score >= 5 ? 'bg-yellow-500/20 text-yellow-800' : 'bg-green-500/10 text-green-600'}`}>{item.score}</span>
                                                
                                            </div>
                                        ))}
                                    </div>
                                )
                            }
                        </div>

                        <div className='w-full rounded-[8px] border border-gray-100 p-4'>
                            <SectionHeader
                                title="Overall Comments"
                                sectionKey="overallComments"
                                action={<button onClick={addOverallComment} className='flex items-center gap-x-1 text-xs text-gray-500 hover:text-gray-700'>
                                    <PlusIcon className='w-4 h-4' /> Add Comment
                                </button>}
                            />
                            {sectionsOpen.overallComments && <div className='flex flex-col gap-y-3'>
                                {compiledResult.overallComments?.length === 0 && <p className='text-xs text-gray-400'>No overall comments recorded.</p>}
                                {compiledResult.overallComments?.slice().reverse().map((comment, reversedIndex) => {
                                    const commentIndex = compiledResult.overallComments.length - 1 - reversedIndex

                                    return (
                                    <div key={commentIndex} className='flex items-center gap-x-2'>
                                        <div className='w-11/12'>
                                            <TextField
                                                inputLabel=""
                                                fieldId={`overall-comment-${commentIndex}`}
                                                hasError={false}
                                                preloadValue={comment || ''}
                                                inputPlaceholder="Comment"
                                                returnFieldValue={(value)=>{updateOverallComment(commentIndex, value)}}
                                            />
                                        </div>
                                        <button onClick={()=>{removeOverallComment(commentIndex)}} className='w-1/12 flex justify-center pt-3 text-gray-400 hover:text-red-600'>
                                            <TrashIcon className='w-4 h-4' />
                                        </button>
                                    </div>
                                    )
                                })}
                            </div>}
                        </div>
                    </div>
                }
            </div>

            <ModalDialog
                shown={confirmModalShown}
                closeFunction={()=>{setConfirmModalShown(false)}}
                dialogTitle={`Confirm Result ${compiledResult?.issues?.length > 0 ? 'Despite Issues' : ''}`}
                maxWidthClass="max-w-md"
                hideActions={true}
            >
                {compiledResult?.issues?.length === 0 && <p className="text-sm">
                    You are about to commit the current compiled results for the student. Please confirm that you have carefully reviewed this result and made all necessary adjustments.
                </p>}

                {compiledResult?.issues?.length > 0 && <p className='text-sm text-gray-600'>
                    This compilation has {compiledResult?.issues?.length} issue{compiledResult?.issues?.length > 1 ? 's' : ''} that may need your attention. Are you sure you want to proceed and save this result anyway?
                </p>}
                {compiledResult?.issues?.length > 0 && (
                    <>
                        <p className='text-sm text-gray-600'>Here's a list of the issues found:</p>
                        {sectionsOpen.issues && <ul className='mt-2 list-disc list-inside text-sm text-amber-700 space-y-1'>
                            {compiledResult.issues.map((issue, issueIndex) => (
                                <li key={issueIndex}>{typeof issue === 'string' ? issue : issue.message}</li>
                            ))}
                        </ul>}
                    </>
                )}
                <div className='mt-8 flex flex-row-reverse gap-x-4'>
                    <ModalActionButton
                        buttonLabel={`Yes, Confirm ${compiledResult?.issues?.length > 0 ? 'Anyway' : 'and Save Result'}`}
                        buttonAction={confirmResults}
                        processing={confirming}
                    />
                    <button
                        type="button"
                        className="inline-flex justify-center px-5 py-4 text-xs font-medium bg-transparent border border-transparent rounded-md hover:bg-opacity-50 hover:bg-secondary focus:outline-none"
                        onClick={()=>{setConfirmModalShown(false)}}
                    >
                        Cancel
                    </button>
                </div>
            </ModalDialog>

            <ModalDialog
                shown={subjectModal.shown}
                closeFunction={closeSubjectModal}
                dialogTitle={subjectModal.areaKey === 'areasOfStrength' ? 'Add Area of Strength' : 'Add Area Needing Improvement'}
                maxWidthClass="max-w-md"
                hideActions={true}
            >
                <div className='mt-2'>
                    <SelectField
                        selectOptions={subjectOptions}
                        inputLabel="Subject"
                        titleField="name"
                        displayImage={false}
                        imageField=""
                        fieldId="add-area-subject"
                        requiredField={true}
                        hasError={false}
                        returnFieldValue={(value)=>{setSubjectModalSubject(value)}}
                    />
                </div>
                <div className='mt-4'>
                    <TextField
                        inputLabel="Comment"
                        fieldId="add-area-subject-comment"
                        hasError={false}
                        preloadValue={subjectModalComment}
                        inputPlaceholder="Comment"
                        returnFieldValue={(value)=>{setSubjectModalComment(value)}}
                    />
                </div>
                <div className='mt-8 flex flex-row-reverse gap-x-4'>
                    <ModalActionButton
                        buttonLabel="Add"
                        buttonAction={confirmAddAreaSubject}
                        processing={false}
                    />
                    <button
                        type="button"
                        className="inline-flex justify-center px-5 py-4 text-xs font-medium bg-transparent border border-transparent rounded-md hover:bg-opacity-50 hover:bg-secondary focus:outline-none"
                        onClick={closeSubjectModal}
                    >
                        Cancel
                    </button>
                </div>
            </ModalDialog>

            <ModalDialog
                shown={traitsModalShown}
                closeFunction={closeTraitsModal}
                dialogTitle="Manage Student Traits"
                maxWidthClass="max-w-lg"
                hideActions={true}
            >
                <ManageStudentTraits 
                    student={student} 
                    close={() => setTraitsModalShown(false)} 
                    reload={() => {setReload(reload => reload + 1)}}
                />
            </ModalDialog>
        </>
    )
}

export default StudentResultsCompilation